import React, { useContext, useMemo, useState } from 'react'

import { faCheck, faKey, faClockThreeThirty } from '@fortawesome/pro-solid-svg-icons'
import { Modal, notification } from 'antd'
import StudentInfo from '../../../Components/shared/StudentInfo'
import moment from 'moment'
import { connect, mapDispatchToProps, mapStateToProps } from '../../../reducers/Dispatchers'
import { generalErrorHandler, objectDeepCopy, request } from '../../../utils'
import AdminResetPassword from '../../../Components/shared/AdminResetPassword'
import { getFullSectionName } from '../../../Components/school/Sections/utils/DataDiverter'
import { formatStudentBirthdateInBackendForm } from '../../../utils/entitiesFormaters/Student'
import { arraySortingByKeyAndType } from '../../../utils/sorting'
import { HttpMethods, onInfo, onSuccess, onWarning } from '../../../utils/apiHelper'
import { isAdmin, isCoordinator, isDomainEvaluator, isEncoder, isSupervisor } from '../../../utils/roles'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import OldStudentsTable from './OldStudentsTable'
import ModalHours from '../../../Components/student/ModalHours'
import { SchoolContext } from '../../../Providers/SchoolProvider'

import '../../../assets/school-students-view.scss'

const Students = props => {
  const { useHoursExport } = useContext(SchoolContext)

  const [selectedStudentInfo, setSelectedStudentInfo] = useState(null)
  const [selectedStudentForPasswordReset, setSelectedStudentForPasswordReset] = useState(null)
  const [refreshingStudents, setRefreshingStudents] = useState(false)
  const [existingStudentData, setExistingStudentData] = useState(null)
  const [displayHoursModal, setDisplayHoursModal] = useState(false)

  const permissions = useMemo(() => ({
    canEditStudent: isAdmin(props.getUser) || isEncoder(props.getUser) || isCoordinator(props.getUser),
    canResetPassword: isAdmin(props.getUser) || isEncoder(props.getUser)
  }), [props.getUser])

  const addStudent = async (student, stopLoadingCallback) => {
    student.birthdate = student.birthdate === null || student.birthdate.trim().length === 0
      ? undefined
      : `${student.birthdate.split('/')[2]}-${student.birthdate.split('/')[1]
        }-${student.birthdate.split('/')[0]}`

    student.stillInSchool = true
    delete student.isActive

    try {
      const { data } = await request('/api/Student', HttpMethods.POST, student, props.getUser)

      if (!data) {
        stopLoadingCallback()

        return
      }

      handleStudentSaving(data)
      showNotificationIfStudentExist(data)
    } catch (err) {
      generalErrorHandler(err)
    }
  }

  const showNotificationIfStudentExist = data => {
    if (data.studentAlreadyExists) {
      setExistingStudentData(data)
    }
  }

  const editStudent = async (student, stopLoadingCallback) => {
    student.birthdate = formatStudentBirthdateInBackendForm(student)

    if (student.user && student.user.id) {
      student.user = student.user.id
    }

    delete student.isActive

    const originalStudentValues = props.getStudents.find(item => item.id === student.id)
    student.originalSection = originalStudentValues.sections && originalStudentValues.sections.length ? originalStudentValues.sections[0].id : null

    try {
      const { data, message } = await request('/api/students/' + student.id, HttpMethods.PATCH, student, props.getUser)

      if (!data) {
        stopLoadingCallback()

        return
      }

      if (message === 'student.edit.only.section') {
        onInfo(props.t('students_view.edit_only_section'))
      } else if (message === 'student.edit.not.allowed') {
        onWarning(props.t('students_view.edit_not_allowed'))
      } else {
        onSuccess(props.t('students_view.edit_success'))
      }

      handleStudentSaving(data, student)
    } catch (err) {
      generalErrorHandler(err)
    }
  }

  const handleStudentSaving = (student, oldStudent = null) => {
    if (student.birthdate) {
      const birthdate = student.birthdate.date ?? student.birthdate

      student.birthdate = { date: birthdate.split('T')[0] }
    }

    oldStudent ? updateStoredStudentData(student, oldStudent) : addNewStudentToStore(student)
  }

  const updateStoredStudentData = (student, oldStudent) => {
    student.user = oldStudent.user

    props.saveStudent(student, true, true)
  }

  const addNewStudentToStore = student => {
    props.saveStudent(student, false, true)
  }

  const deleteStudent = student => {
    props.removeStudent({
      studentId: student.id,
      currentUser: props.getUser
    })
    notification.success({ message: props.t('Recorded'), placement: 'bottomRight' })
  }

  const handleCloseStudentExistsNotification = () => {
    setExistingStudentData(null)
  }

  const formatSchoolSectionForSmartable = schoolSection => ({
    id: schoolSection.id,
    name: isAdmin(props.getUser) || isEncoder(props.getUser) || isCoordinator(props.getUser) || isSupervisor(props.getUser) || isDomainEvaluator(props.getUser)
      ? getFullSectionName(schoolSection)
      : schoolSection.name,
    nameTitle: getFullSectionName(schoolSection, false, false)
  })

  const sections = useMemo(() => {
    if (props.getSchools?.length <= 0) return []

    const school = props.getSchools[0]

    let sections = props.getSchoolSections[school.id]
      ? objectDeepCopy(props.getSchoolSections[school.id])
      : []

    if (!isEncoder(props.getUser)) {
      sections.unshift(school.rootSection)
    }

    sections = sections.map(s => formatSchoolSectionForSmartable(s))
    sections = arraySortingByKeyAndType(sections, 'name', 'string')

    return sections
  }, [props.getSchools, props.getSchoolSections, props.getUser])

  const additionalActions = useMemo(() => {
    const actions = [
      {
        iconName: 'user-graduate',
        type: 'primary',
        title: props.t('Student sheet'),
        onClick: setSelectedStudentInfo
      }
    ]

    if (permissions.canResetPassword) {
      actions.push({
        iconName: faKey,
        type: 'primary',
        title: props.t('Reset password'),
        onClick: setSelectedStudentForPasswordReset,
        disabledCallback: s => s.user?.isExternal,
        titleCallback: s => s.user?.isExternal ? props.t('This user is external and cannot have its password reset') : ''
      })
    }

    return actions
  }, [props.t, setSelectedStudentInfo, permissions])

  const reloadStudentsAfterImport = async () => {
    setRefreshingStudents(true)
    await props.fetchStudents(props.getUser)
    setRefreshingStudents(false)
  }

  return (
    <div>
      <OldStudentsTable
        additionalActions={additionalActions}
        additionalExports={useHoursExport ? [{ onClick: () => setDisplayHoursModal(true), icon: faClockThreeThirty, label: props.t('students_view.export.hours') }] : []}
        sections={sections}
        students={props.getStudents}
        loading={!props.getDataReady.students || refreshingStudents}
        onDataAdd={permissions.canEditStudent ? addStudent : undefined}
        onDataEdit={permissions.canEditStudent ? editStudent : undefined}
        onDataDelete={permissions.canEditStudent ? deleteStudent : undefined}
        reload={reloadStudentsAfterImport}
      />
      <StudentInfo
        student={selectedStudentInfo}
        onClose={() => setSelectedStudentInfo(null)}
      />
      {permissions.canResetPassword &&
        <AdminResetPassword
          userToResetPassword={selectedStudentForPasswordReset}
          onClose={() => setSelectedStudentForPasswordReset(null)}
        />}
      <Modal
        title={props.t('Student already exists')}
        visible={existingStudentData !== null}
        onCancel={handleCloseStudentExistsNotification}
        footer={[
          <IconButton
            type='primary'
            key='close'
            icon={faCheck}
            text={props.t('OK')}
            onClick={handleCloseStudentExistsNotification}
          />
        ]}
      >
        {existingStudentData && (
          <>
            {/* eslint-disable-next-line */}
            <h3>{props.t('We found an existing student for email ${email}', { email: existingStudentData.email })}</h3>
            <div>{props.t('Below you can see the details of the existing student:')}</div>
            <ul className='existing-student-details'>
              <li>
                <label>{props.t('Lastname')}</label>
                <b>{existingStudentData.lastname || props.t('None')}</b>
              </li>
              <li>
                <label>{props.t('Firstname')}</label>
                <b>{existingStudentData.firstname || props.t('None')}</b>
              </li>
              <li>
                <label>{props.t('Registration number')}</label>
                <b>{existingStudentData.phoneNumber || props.t('None')}</b>
              </li>
              <li>
                <label>{props.t('Phone')}</label>
                <b>{existingStudentData.matricule || props.t('None')}</b>
              </li>
              <li>
                <label>{props.t('Birthdate')}</label>
                <b>{existingStudentData.birthdate ? moment(existingStudentData.birthdate.date).format('DD/MM/YYYY') : props.t('None')}</b>
              </li>
            </ul>
            <h3><b>{props.t('This existing student was linked to the section you selected.')}</b></h3>
          </>
        )}
      </Modal>
      <ModalHours
        visible={displayHoursModal}
        onClose={() => setDisplayHoursModal(false)}
      />
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(Students)
